import React, { useState, useEffect, useCallback } from 'react';
import { Globe2, Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { api } from '../../lib/api';
import { STAGE_SHORT } from './ImportCategoriesCard';

const fmt = (n) => (n || 0).toLocaleString('de-DE');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
const monthLabel = (m) => { const [y, mo] = m.split('-'); return `${mo}/${y.slice(2)}`; };

function Kpi({ label, value, hint, testid }) {
  return (
    <div className="bg-secondary/60 border border-border rounded-sm px-3 py-2" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground/70 font-bold">{label}</p>
      <p className="font-mono text-xl text-foreground leading-tight">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function GrowthChart({ growth }) {
  if (!growth?.length) return null;
  const data = growth.slice(-18).map(g => ({ ...g, label: monthLabel(g.month) }));
  return (
    <div data-testid="universe-growth-chart">
      <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest font-bold mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Wachstum pro Monat (neu hinzugefügt · kumuliert)</p>
      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(148,163,184,0.12)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', fontSize: 11 }} formatter={(v, n) => [fmt(v), n === 'added' ? 'neu' : 'kumuliert']} />
            <Bar yAxisId="l" dataKey="added" fill="#3b82f6" radius={[2, 2, 0, 0]} />
            <Line yAxisId="r" type="monotone" dataKey="cumulative" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function TargetUniverseCard({ refreshKey }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.getImportCategoryStats().then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  const lastMonth = stats?.growth?.length ? stats.growth[stats.growth.length - 1] : null;
  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-5" data-testid="target-universe-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg text-foreground flex items-center gap-2"><Globe2 className="w-5 h-5 text-amber-400" /> Target Universe</h2>
          <p className="text-xs text-muted-foreground mt-1">Wie das Zieluniversum wächst: Deals pro Kategorie, wie viele noch in der Start-Stufe liegen, Import-Historie.</p>
        </div>
        <button onClick={load} className="text-muted-foreground hover:text-foreground p-1" title="Aktualisieren" data-testid="universe-refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>
      {!stats ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Kpi label="Universe gesamt" value={fmt(stats.total_universe)} hint="alle Deals in der Datenbank" testid="universe-total" />
            <Kpi label="Mit Kategorie" value={fmt(stats.categorized_total)} hint={`${fmt(stats.uncategorized?.total)} ohne Kategorie`} testid="universe-categorized" />
            <Kpi label="Kategorien" value={fmt(stats.categories.length)} hint={`${stats.categories.filter(c => c.archived).length} archiviert`} testid="universe-categories" />
            <Kpi label={lastMonth ? `Neu im ${monthLabel(lastMonth.month)}` : 'Neu zuletzt'} value={fmt(lastMonth?.added)} hint={`${fmt(stats.batches.length)} Importe insgesamt`} testid="universe-last-month" />
          </div>

          {stats.categories.length > 0 && (
            <div className="overflow-x-auto" data-testid="universe-category-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[9px] uppercase tracking-widest text-muted-foreground/70 border-b border-border">
                    <th className="text-left py-1.5 font-bold">Kategorie</th>
                    <th className="text-left py-1.5 font-bold">Start-Stufe</th>
                    <th className="text-right py-1.5 font-bold">Gesamt</th>
                    <th className="text-right py-1.5 font-bold">In Start-Stufe</th>
                    <th className="text-right py-1.5 font-bold">Weitergezogen</th>
                    <th className="text-right py-1.5 font-bold">Passed</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.categories.map(c => (
                    <tr key={c.id} className={`border-b border-border/50 ${c.archived ? 'opacity-50' : ''}`} data-testid={`universe-row-${c.id}`}>
                      <td className="py-1.5 text-foreground">{c.name}{c.archived && <span className="ml-1 text-[9px] font-mono text-muted-foreground uppercase">archiviert</span>}</td>
                      <td className="py-1.5 text-muted-foreground">{STAGE_SHORT[c.stage] || c.stage_label}</td>
                      <td className="py-1.5 text-right font-mono text-foreground" data-testid={`universe-total-${c.id}`}>{fmt(c.total)}</td>
                      <td className="py-1.5 text-right font-mono text-muted-foreground">{fmt(c.in_start_stage)}</td>
                      <td className="py-1.5 text-right font-mono text-emerald-300">{fmt(c.progressed)}</td>
                      <td className="py-1.5 text-right font-mono text-red-300/80">{fmt(c.passed)}</td>
                    </tr>
                  ))}
                  <tr className="text-muted-foreground" data-testid="universe-row-none">
                    <td className="py-1.5 italic">Ohne Kategorie</td>
                    <td className="py-1.5">—</td>
                    <td className="py-1.5 text-right font-mono">{fmt(stats.uncategorized.total)}</td>
                    <td className="py-1.5 text-right font-mono">—</td>
                    <td className="py-1.5 text-right font-mono">—</td>
                    <td className="py-1.5 text-right font-mono">{fmt(stats.uncategorized.passed)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <GrowthChart growth={stats.growth} />

          <div data-testid="universe-import-history">
            <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest font-bold mb-1">Import-Historie</p>
            {!stats.batches.length ? <p className="text-xs text-muted-foreground italic">Noch kein Batch-Upload erfasst.</p> : (
              <div className="max-h-56 overflow-y-auto custom-scrollbar">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-[9px] uppercase tracking-widest text-muted-foreground/70 border-b border-border">
                      <th className="text-left py-1 font-bold">Wann</th>
                      <th className="text-left py-1 font-bold">Wer</th>
                      <th className="text-left py-1 font-bold">Datei</th>
                      <th className="text-left py-1 font-bold">Kategorie → Stufe</th>
                      <th className="text-right py-1 font-bold">Importiert</th>
                      <th className="text-right py-1 font-bold">Duplikate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.batches.map(b => (
                      <React.Fragment key={b.id}>
                      <tr className="border-b border-border/40" data-testid={`import-batch-${b.id}`}>
                        <td className="py-1 text-muted-foreground whitespace-nowrap">{fmtDate(b.created_at)}</td>
                        <td className="py-1 text-foreground/80">{b.by_name}</td>
                        <td className="py-1 text-muted-foreground truncate max-w-[180px]" title={b.file_name}>{b.file_name}</td>
                        <td className="py-1 text-foreground/80">{b.category_name ? `${b.category_name} → ` : ''}{STAGE_SHORT[b.stage] || b.stage_label}</td>
                        <td className="py-1 text-right font-mono text-emerald-300">
                          {fmt(b.created)}
                          {b.status === 'running' && <span className="ml-1 text-[9px] text-amber-300" data-testid={`import-batch-running-${b.id}`}>läuft…</span>}
                          {b.status === 'error' && <span className="ml-1 text-[9px] text-red-300" title={b.error} data-testid={`import-batch-error-${b.id}`}>abgebrochen</span>}
                        </td>
                        <td className="py-1 text-right font-mono text-muted-foreground">{fmt(b.skipped_duplicates)}</td>
                      </tr>
                      {b.status === 'error' && (
                        <tr className="border-b border-border/40" data-testid={`import-batch-error-row-${b.id}`}>
                          <td colSpan={6} className="pb-1.5 text-[10px] text-red-300/90">
                            {b.error}{b.total_rows ? ` (${fmt(b.processed || 0)} von ${fmt(b.total_rows)} Zeilen verarbeitet)` : ''}
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
